import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Ellipsis,
  Eye,
  Search,
  FileText,
  Pencil,
  Trash2,
  X,
  Funnel,
  Plus,
} from "lucide-react";
import Layout from "../../components/Layout";
import CategoryDropdown from "../../components/CategoryDropdown";
import Pagination from "../../components/Pagination";
import { formatDateTime } from "../../utils/dateFormatter";
import StatusDropdown from "../../components/StatusDropdown";
import ConfirmDelete from "../../components/ConfirmDelete";
import { PostsService, Post } from "../../services/postsService";
import { CategoriesService } from "../../services/categoriesService";
import { toAbsoluteUrl } from "../../config/api";
import "./../../styles/member/article.css";
import defaultThumbnail from "../../assets/images/default-thumbnail.webp";

const Article = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Post[]>([]);
  const [allArticles, setAllArticles] = useState<Post[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [query, setQuery] = useState("");
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const [viewArticle, setViewArticle] = useState<Post | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [stats, setStats] = useState({
    approved: 0,
    pending: 0,
    rejected: 0,
  });
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const menuRef = useRef<HTMLDivElement | null>(null);

  // Tính toán phân trang
  const totalPages = Math.ceil(articles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentArticles = articles.slice(startIndex, endIndex);

  // Tính toán trạng thái chọn tất cả
  const allSelected =
    currentArticles.length > 0 && selectedIds.length === currentArticles.length;
  const someSelected =
    selectedIds.length > 0 && selectedIds.length < currentArticles.length;

  // Xử lý chọn tất cả
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(currentArticles.map((article) => article.id));
    }
  };

  // Xử lý chọn một bài viết
  const toggleSelect = (articleId: number) => {
    setSelectedIds((prev) =>
      prev.includes(articleId)
        ? prev.filter((id) => id !== articleId)
        : [...prev, articleId]
    );
  };

  // Tải bài viết từ API
  const loadArticles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await PostsService.getMyPosts({
        page: 1,
        limit: 1000, // Tăng limit để lấy tất cả bài viết
      });

      setArticles(response.posts);
      setAllArticles(response.posts);

      // Tính toán số liệu thống kê
      const approved = response.posts.filter(
        (p) => p.status === "approved"
      ).length;
      const pending = response.posts.filter(
        (p) => p.status === "pending"
      ).length;
      const rejected = response.posts.filter(
        (p) => p.status === "rejected"
      ).length;
      setStats({ approved, pending, rejected });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Có lỗi xảy ra khi tải danh sách bài viết"
      );
    } finally {
      setLoading(false);
    }
  };

  // Tải danh mục
  const loadCategories = async () => {
    try {
      const response = await CategoriesService.listCategories({ limit: 100 });
      setCategories(response.data);
    } catch (err) {}
  };

  // Tìm kiếm
  const handleSearch = () => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setArticles(allArticles);
    } else {
      setArticles(
        allArticles.filter(
          (a) =>
            a.title.toLowerCase().includes(q) ||
            a.content.toLowerCase().includes(q) ||
            (a.author?.fullName || a.author?.email || "")
              .toLowerCase()
              .includes(q)
        )
      );
    }
    setCurrentPage(1); // Reset về trang đầu khi tìm kiếm
  };

  // Lọc danh mục
  const handleCategory = (cat: string) => {
    if (cat === "Tất cả") {
      setArticles(allArticles);
    } else {
      setArticles(allArticles.filter((n) => n.category?.name === cat));
    }
    setCurrentPage(1); // Reset về trang đầu khi lọc
  };

  // Lọc trạng thái
  const handleStatus = (status: string) => {
    if (status === "Tất cả") {
      setArticles(allArticles);
    } else {
      let statusValue = "";
      switch (status) {
        case "Chờ Duyệt":
          statusValue = "pending";
          break;
        case "Đã duyệt":
          statusValue = "approved";
          break;
        case "Từ chối":
          statusValue = "rejected";
          break;
        default:
          statusValue = "";
      }
      setArticles(allArticles.filter((n) => n.status === statusValue));
    }
    setCurrentPage(1); // Reset về trang đầu khi lọc
  };

  // Click ngoài đóng menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Tải dữ liệu khi component mount
  useEffect(() => {
    loadArticles();
    loadCategories();
  }, []);

  // Tải lại dữ liệu khi focus vào trang (để cập nhật sau khi edit)
  useEffect(() => {
    const handleFocus = () => {
      loadArticles();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  // Xác nhận xoá
  const onDelete = (id: number) => {
    setDeleteId(id);
    setShowDelete(true);
  };

  const onConfirmDelete = async () => {
    if (deleteId == null) return;
    setLoading(true);
    setError(null);
    try {
      await PostsService.softDeletePost(deleteId);
      setArticles((prev) => prev.filter((a) => a.id !== deleteId));
      setAllArticles((prev) => prev.filter((a) => a.id !== deleteId));
      setShowDelete(false);
      setDeleteId(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Có lỗi xảy ra khi xóa bài viết"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (articleId: number) => {
    navigate(`/member/edit-article/${articleId}`);
  };

  // Xử lý xóa hàng loạt
  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setShowBulkDelete(true);
  };

  const onConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const result = await PostsService.bulkSoftDeletePosts(selectedIds);
      // Cập nhật danh sách bài viết sau khi xóa
      setArticles((prev) => prev.filter((a) => !selectedIds.includes(a.id)));
      setAllArticles((prev) => prev.filter((a) => !selectedIds.includes(a.id)));
      setSelectedIds([]);
      setShowBulkDelete(false);
      // Tải lại dữ liệu để cập nhật thống kê
      await loadArticles();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Có lỗi xảy ra khi xóa bài viết";
      setError(errorMessage);
      // Nếu có một số bài viết đã được xóa thành công, cập nhật lại danh sách
      if (errorMessage.includes("Xóa thành công")) {
        await loadArticles();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="article-page">
        {/* Header */}
        <div className="article-header">
          <div>
            <h2>Quản lý bài viết</h2>
            <p>Theo dõi và quản lý tất cả các bài viết</p>
          </div>
          <div>
            <Link
              to="/member/create-article"
              className="btn-primary btn-add-custom"
            >
              <Plus size={16} /> Tạo bài viết
            </Link>
          </div>
        </div>

        {/* Bộ lọc */}
        <div className="article-filter ">
          <div className="article-filter-title">
            <Funnel size={20} /> Bộ Lọc
          </div>
          <div className="article-filter-bar">
            <StatusDropdown onSelect={handleStatus} />
            <CategoryDropdown
              onSelect={handleCategory}
              categories={categories}
            />
            <div className="search-box">
              <Search className="search-icon" size={18} />
              <input
                type="text"
                placeholder="Tìm kiếm bài viết..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
            </div>
          </div>
        </div>

        {/* Thông báo lỗi */}
        {error && <div className="error-message">{error}</div>}

        {/* Danh sách bài viết */}
        <div className="article-list">
          <div className="list-header">
            <h3>Danh sách bài viết ({articles.length})</h3>
            <span>
              {stats.approved} đã duyệt • {stats.pending} chờ duyệt •{" "}
              {stats.rejected} từ chối
            </span>
          </div>

          {/* Bulk actions */}
          {selectedIds.length > 0 && (
            <div className="cat-bulk-bar">
              <span>Đã chọn {selectedIds.length}</span>
              <div className="card-actions">
                <button
                  type="button"
                  className="status-reject"
                  disabled={loading}
                  onClick={handleBulkDelete}
                >
                  <Trash2 size={16} />
                  Xóa tất cả
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setSelectedIds([])}
                >
                  Bỏ chọn
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="loading-state">Đang tải...</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = someSelected;
                      }}
                      onChange={toggleSelectAll}
                      aria-label="Chọn tất cả"
                    />
                  </th>
                  <th>ID</th>
                  <th>Bài viết</th>
                  <th>Danh mục</th>
                  <th>Trạng thái</th>
                  <th>Lượt xem</th>
                  <th>Ngày đăng</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {currentArticles.map((a, i) => (
                  <tr key={a.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(a.id)}
                        onChange={() => toggleSelect(a.id)}
                        aria-label={`Chọn bài viết ${a.title}`}
                      />
                    </td>
                    <td>AM{a.id.toString().padStart(3, "0")}</td>
                    <td>
                      <div className="article-info">
                        {/* <img
                          src={a.featuredImage || "/images/default-article.jpg"}
                          alt={a.title}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "/images/default-article.jpg";
                          }}
                        /> */}
                        <img
                          src={
                            a.featuredImage && a.featuredImage.trim()
                              ? a.featuredImage.startsWith('http')
                                ? a.featuredImage
                                : toAbsoluteUrl(a.featuredImage)
                              : defaultThumbnail
                          }
                          alt={a.title || "Ảnh bài viết"}
                          className="article-thumbnail"
                          onError={(e) => {
                            const img = e.currentTarget as HTMLImageElement;
                            if (img.src !== defaultThumbnail) {
                              img.src = defaultThumbnail;
                            }
                          }}
                        />
                        <div>
                          <p className="title">{a.title}</p>
                          <span className="link">{a.slug}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="tag">
                        {a.category?.name || "Chưa phân loại"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`status ${
                          a.status === "approved"
                            ? "approved"
                            : a.status === "pending"
                            ? "pending"
                            : "rejected"
                        }`}
                        title={
                          a.status === "approved"
                            ? "Đã duyệt"
                            : a.status === "pending"
                            ? "Chờ duyệt"
                            : "Từ chối"
                        }
                      >
                        {a.status === "approved"
                          ? "Đã duyệt"
                          : a.status === "pending"
                          ? "Chờ duyệt"
                          : "Từ chối"}
                      </span>
                    </td>
                    <td>
                      <span className="views">
                        <Eye size={16} /> {a.viewCount}
                      </span>
                    </td>
                    <td>{formatDateTime(a.createdAt)}</td>
                    <td className="action-table">
                      <button
                        className="action-btn1"
                        onClick={() =>
                          setOpenMenu(
                            openMenu === startIndex + i ? null : startIndex + i
                          )
                        }
                      >
                        <Ellipsis size={18} />
                      </button>
                      {openMenu === startIndex + i && (
                        <div className="action-menu" ref={menuRef}>
                         <div
                            className="action-item"
                            onClick={() => navigate(`/posts/detail/${a.id}`)}
                          >
                            <FileText size={16} /> Xem bài viết
                          </div>
                          <div
                            className="action-item"
                            onClick={() => handleEdit(a.id)}
                          >
                            <Pencil size={16} /> Chỉnh sửa
                          </div>
                          <div
                            className="action-item delete"
                            onClick={() => onDelete(a.id)}
                          >
                            <Trash2 size={16} /> Xoá
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Phân trang */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>

        {/* Modal xem chi tiết bài viết */}
        {viewArticle && (
          <div className="modal-overlay">
            <div className="article-detail-modal">
              <div className="article-detailed-header">
                <h3>Chi tiết bài viết</h3>
                <button
                  className="modal-close"
                  onClick={() => setViewArticle(null)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="article-detailed-content">
                {/* Thông tin cơ bản */}
                <div className="detailed-section">
                  <div className="detailed-section-info">
                    <h4>Thông tin cơ bản</h4>
                    <div className="detailed-section-row">
                      <div className="title-article">
                        <strong>ID:</strong>
                        <p className="detail-text">
                          AM{viewArticle.id.toString().padStart(3, "0")}
                        </p>
                      </div>
                      <div className="slug-article">
                        <strong>Slug:</strong>
                        <p className="detail-slug">{viewArticle.slug}</p>
                      </div>
                    </div>
                    <div className="detailed-section-row">
                      <div className="title-article">
                        <strong>Tiêu đề:</strong>
                        <p className="detail-text">{viewArticle.title}</p>
                      </div>
                    </div>
                    <div className="detailed-section-row">
                      <div>
                        <strong>Tác giả:</strong>
                        <p className="detail-text">
                          {viewArticle.author?.fullName ||
                            viewArticle.author?.email ||
                            "N/A"}
                        </p>
                      </div>
                      <div>
                        <strong>Danh mục:</strong>
                        <p className="detail-text">
                          {viewArticle.category?.name || "Chưa phân loại"}
                        </p>
                      </div>
                    </div>
                    <div className="detailed-section-row">
                      <div>
                        <strong>Trạng thái:</strong>
                        <p className="detail-text">
                          <span
                            className={`status ${
                              viewArticle.status === "approved"
                                ? "approved"
                                : viewArticle.status === "pending"
                                ? "pending"
                                : "rejected"
                            }`}
                            title={
                              viewArticle.status === "approved"
                                ? "Đã duyệt"
                                : viewArticle.status === "pending"
                                ? "Chờ duyệt"
                                : "Từ chối"
                            }
                          >
                            {viewArticle.status === "approved"
                              ? "Đã duyệt"
                              : viewArticle.status === "pending"
                              ? "Chờ duyệt"
                              : "Từ chối"}
                          </span>
                        </p>
                      </div>
                      <div>
                        <strong>Lượt xem:</strong>
                        <p className="detail-text">{viewArticle.viewCount}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Ảnh đại diện */}
                {viewArticle.featuredImage && (
                  // <div className="detailed-section">
                  //   <div className="detailed-section-info">
                  //     <h4>Ảnh đại diện</h4>
                  //     <img
                  //       src={viewArticle.featuredImage}
                  //       alt={viewArticle.title}
                  //       className="detail-featured-image"
                  //       onError={(e) => {
                  //         (e.target as HTMLImageElement).style.display = "none";
                  //       }}
                  //     />
                  //   </div>
                  // </div>
                  <div className="detailed-section">
                    <div className="detailed-section-info">
                      <h4>Ảnh đại diện</h4>
                      <img
                        src={
                          viewArticle.featuredImage && viewArticle.featuredImage.trim()
                            ? viewArticle.featuredImage.startsWith('http')
                              ? viewArticle.featuredImage
                              : toAbsoluteUrl(viewArticle.featuredImage)
                            : defaultThumbnail
                        }
                        alt={viewArticle.title}
                        className="detail-featured-image"
                        onError={(e) => {
                          const img = e.currentTarget as HTMLImageElement;
                          if (img.src !== defaultThumbnail) {
                            img.src = defaultThumbnail;
                          }
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Thông tin SEO */}
                <div className="detailed-section">
                  <div className="detailed-section-info">
                    <h4>Thông tin SEO</h4>
                    <div className="meta-article">
                      {viewArticle.metaDescription && (
                        <div className="meta-description-article">
                          <strong>Meta Description:</strong>
                          <p className="meta-description-text">
                            {viewArticle.metaDescription}
                          </p>
                        </div>
                      )}
                      {viewArticle.primaryKeyword && (
                        <div className="meta-description-article">
                          <strong>Từ khóa chính:</strong>
                          <p>{viewArticle.primaryKeyword}</p>
                        </div>
                      )}
                      {viewArticle.secondaryKeywords &&
                        viewArticle.secondaryKeywords.length > 0 && (
                          <div className="meta-description-article">
                            <strong>Từ khóa phụ:</strong>
                            <p>{viewArticle.secondaryKeywords.join(", ")}</p>
                          </div>
                        )}
                      {/* {viewArticle.anchorText && (
                        <div className="meta-description-article">
                          <strong>Anchor Text:</strong>
                          <p>{viewArticle.anchorText}</p>
                        </div>
                      )} */}
                    </div>
                  </div>
                </div>

                {/* Nội dung bài viết */}
                <div className="detailed-section">
                  <div className="detailed-section-info">
                    <h4>Nội dung bài viết</h4>
                    <div
                      className="article-content-display"
                      dangerouslySetInnerHTML={{ __html: viewArticle.content }}
                    />
                  </div>
                </div>

                {/* Liên kết */}
                {((viewArticle.internalLinks &&
                  viewArticle.internalLinks.length > 0) ||
                  (viewArticle.externalLinks &&
                    viewArticle.externalLinks.length > 0)) && (
                  <div className="detailed-section">
                    <div className="detailed-section-info">
                      <h4>Liên kết</h4>
                      <div className="link-meta-article">
                        {viewArticle.internalLinks &&
                          viewArticle.internalLinks.length > 0 && (
                            <div>
                              <strong>Liên kết nội bộ:</strong>
                              <ul className="links-list">
                                {viewArticle.internalLinks.map(
                                  (link, index) => (
                                    <li key={index} className="link-item">
                                      <a
                                        href={link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="link-url"
                                      >
                                        {link}
                                      </a>
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          )}
                        {viewArticle.externalLinks &&
                          viewArticle.externalLinks.length > 0 && (
                            <div>
                              <strong>Liên kết bên ngoài:</strong>
                              <ul className="links-list">
                                {viewArticle.externalLinks.map(
                                  (link, index) => (
                                    <li key={index} className="link-item">
                                      <a
                                        href={link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="link-url"
                                      >
                                        {link}
                                      </a>
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Tags */}
                {viewArticle.tags && viewArticle.tags.length > 0 && (
                  <div className="detailed-section">
                    <div className="detailed-section-info">
                      <h4>Tags</h4>
                      <div className="tags-container">
                        {viewArticle.tags.map((tag, index) => (
                          <span key={index} className="tag-item">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Thông tin duyệt */}
                {viewArticle.status === "rejected" &&
                  viewArticle.rejectionReason && (
                    <div className="detailed-section">
                      <div className="detailed-section-info">
                        <h4 className="rejection-title">Lý do từ chối</h4>
                        <p className="rejection-reason">
                          {viewArticle.rejectionReason}
                        </p>
                      </div>
                    </div>
                  )}

                {/* Thông tin thời gian */}
                <div className="detailed-section">
                  <div className="detailed-section-info">
                    <h4>Thông tin thời gian</h4>
                    <div className="time-info-row">
                      <div className="time-info-article">
                        <strong>Ngày tạo:</strong>
                        <p className="detail-text">
                          {formatDateTime(viewArticle.createdAt)}
                        </p>
                      </div>
                      <div className="time-info-article">
                        <strong>Ngày cập nhật:</strong>
                        <p className="detail-text">
                          {formatDateTime(viewArticle.updatedAt)}
                        </p>
                      </div>
                      {viewArticle.approvedAt && (
                        <div className="time-info-article">
                          <strong>Ngày duyệt:</strong>
                          <p className="detail-text">
                            {formatDateTime(viewArticle.approvedAt)}
                          </p>
                        </div>
                      )}
                      {viewArticle.approver && (
                        <div>
                          <strong>Người duyệt:</strong>
                          <p className="detail-text">
                            {viewArticle.approver.fullName ||
                              viewArticle.approver.email}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <ConfirmDelete
          isOpen={showDelete}
          title="Xác nhận xóa bài viết"
          message={(() => {
            const article = articles.find((a) => a.id === deleteId);
            return article ? (
              <span>
                Bạn có chắc chắn muốn xóa bài viết <b>{article.title}</b>? Hành
                động này không thể hoàn tác.
              </span>
            ) : (
              "Bạn có chắc chắn muốn xóa bài viết này? Hành động này không thể hoàn tác."
            );
          })()}
          confirmText="Xóa"
          cancelText="Hủy"
          isSubmitting={loading}
          onConfirm={onConfirmDelete}
          onCancel={() => {
            setShowDelete(false);
            setDeleteId(null);
          }}
        />

        <ConfirmDelete
          isOpen={showBulkDelete}
          title="Xác nhận xóa hàng loạt"
          message={
            <span>
              Bạn có chắc chắn muốn xóa <b>{selectedIds.length}</b> bài viết đã
              chọn? Hành động này không thể hoàn tác.
            </span>
          }
          confirmText="Xóa tất cả"
          cancelText="Hủy"
          isSubmitting={loading}
          onConfirm={onConfirmBulkDelete}
          onCancel={() => {
            setShowBulkDelete(false);
          }}
        />
      </div>
    </Layout>
  );
};

export default Article;
